/**
 * Earthworm 游戏模块 Vuex Store
 */

import { DEFAULT_PLAYBACK_SPEAKER, DEFAULT_TTS_PROVIDER } from '@/utils/ttsPlaybackResolve'
import { DEFAULT_TTS_UI_SPEED } from '@/utils/ttsRequest'
import { resolveExpectedAnswerText } from '@/utils/questionV2Tokens'

// 游戏模式枚举
export const GamePlayMode = {
  Dictation: 'DICTATION',
  ChineseToEnglish: 'CHINESE_TO_ENGLISH',
  WordCard: 'WORD_CARD',
  WordFollow: 'WORD_FOLLOW',
  Listening: 'LISTENING'
}

// 题/答状态机（对应老项目的 GameMode）
export const GameMode = {
  Question: 'question',
  Answer: 'answer'
}

const state = {
  // 当前游戏模式
  gamePlayMode: GamePlayMode.ChineseToEnglish,
  // 当前题目状态：'question' 或 'answer'（对齐老项目）
  gameMode: GameMode.Question,
  // 当前题目索引
  statementIndex: 0,
  // 题目列表
  statements: [],
  // 是否显示暂停弹窗
  showGamePauseModal: false,
  // 是否显示设置弹窗
  showSettingModal: false,
  // 是否显示总结弹窗
  showSummaryModal: false,
  // 听写工具栏配置
  dictationToolbar: JSON.parse(localStorage.getItem('dictationToolbar') || '{}'),
  // 是否显示答案提示
  showAnswerTip: false,
  // 错误提示设置
  showErrorTipSetting: true,
  // 播放设置（SettingModal 保存后写入；Game 通过 playbackSettingsRevision 监听同步到本地 state）
  playbackSettings: {
    ttsProvider: DEFAULT_TTS_PROVIDER,
    speaker: DEFAULT_PLAYBACK_SPEAKER,
    speakerVolume: 1,
    speakerSpeed: DEFAULT_TTS_UI_SPEED,
    speakerAutoRead: false,
    speakerAnswerAutoRead: false,
    speakerAnswerTipAutoRead: true,
    // 0=区分大小写，1=不区分大小写（默认 1：句子练习首字母大小写不应导致误判）
    answer_ignore_case: 1,
    speakerWordCardRead: true,
    speakerReadCount: 1,
    speakerReadGap: 3,
    // 逐词卡片模式：toggle=整句/卡片互斥切换；stack=整句常驻+下方展开收起
    answerTipWordCardsLayout: 'stack',
    inputWordChineseHint: false,
    answerAreaShowPhonetic: true,
    answerAreaPhoneticScale: 1,
    answerAreaShowImage: true,
    answerContentShowImage: true,
    answerAreaShowPosLamp: true,
    wordCardAutoNextEnabled: false,
    wordCardAutoNextSec: 5,
    /** 输入类模式答对后自动下一题（不进入答案态，仍播放答题反馈） */
    correctAutoNextEnabled: false,
    /** 未开答案弹窗累计答对 5 次自动进已掌握 */
    correctAutoMasterEnabled: false,
    /** 艾宾浩斯记忆曲线开关 */
    ebbinghausEnable: false,
    feedbackEffectType: 'gif',
    feedbackProblemGifStyle: 'confetti_p_dual_corner',
    summaryEndGifStyle: 'confetti_e_dual_corner'
  },
  /** 每次 SET_DICTATION_TOOLBAR 递增，供查词页等监听同步 */
  dictationToolbarRevision: 0,
  /** 每次 SET_PLAYBACK_SETTINGS 递增，供 Game 监听以同步本地 state */
  playbackSettingsRevision: 0
}

const getters = {
  // 游戏模式选项
  gamePlayModeOptions: () => [
    { label: '中译英', value: GamePlayMode.ChineseToEnglish },
    { label: '卡片跟读', value: GamePlayMode.WordCard },
  ],
  // 当前题目
  currentStatement: (state) => {
    return state.statements[state.statementIndex] || null
  },
  // 是否为听写模式
  isDictationMode: (state) => {
    return state.gamePlayMode === GamePlayMode.Dictation
  },
  // 是否为中译英模式
  isChineseToEnglishMode: (state) => {
    return state.gamePlayMode === GamePlayMode.ChineseToEnglish
  },
  // 是否为题目状态（需要输入）
  isQuestion: (state) => {
    return state.gameMode === GameMode.Question
  },
  // 是否为答案状态（显示答案）
  isAnswer: (state) => {
    return state.gameMode === GameMode.Answer
  },
  // 是否启用错误提示
  isErrorTipEnabled: (state) => {
    return state.showErrorTipSetting
  }
}

const mutations = {
  SET_GAME_PLAY_MODE(state, mode) {
    state.gamePlayMode = mode
  },
  SET_STATEMENT_INDEX(state, index) {
    state.statementIndex = index
  },
  NEXT_STATEMENT(state) {
    if (state.statementIndex < state.statements.length - 1) {
      state.statementIndex++
    }
    // 关键修复：对齐老项目，切题时不重置状态，保持当前状态
  },
  PREV_STATEMENT(state) {
    if (state.statementIndex > 0) {
      state.statementIndex--
    }
    // 关键修复：对齐老项目，切题时不重置状态，保持当前状态
  },
  SET_STATEMENTS(state, statements) {
    state.statements = statements || []
  },
  /** 从当前练习列表移除指定题目（如标记「掌握」后同步剔除） */
  REMOVE_STATEMENT_AT(state, index) {
    const i = Number(index)
    if (i < 0 || i >= state.statements.length) return
    const next = state.statements.slice()
    next.splice(i, 1)
    state.statements = next
    if (state.statements.length === 0) {
      state.statementIndex = 0
    } else if (state.statementIndex >= state.statements.length) {
      state.statementIndex = state.statements.length - 1
    }
  },
  /** 跟句/拆句：按主句/主词 id 移除展开列表中的全部对应项 */
  REMOVE_STATEMENTS_BY_MAIN_QUESTION_ID(state, mainQuestionId) {
    const mid = String(mainQuestionId ?? '').trim()
    if (!mid) return
    const next = (state.statements || []).filter((item) => {
      const parentId = item && item._parentQuestion && item._parentQuestion.id
      const id = String(parentId ?? item?.id ?? item?.question_id ?? '')
      return id !== mid
    })
    state.statements = next
    if (state.statements.length === 0) {
      state.statementIndex = 0
    } else if (state.statementIndex >= state.statements.length) {
      state.statementIndex = state.statements.length - 1
    }
  },
  OPEN_PAUSE_MODAL(state) {
    state.showGamePauseModal = true
  },
  CLOSE_PAUSE_MODAL(state) {
    state.showGamePauseModal = false
  },
  OPEN_SETTING_MODAL(state) {
    state.showSettingModal = true
  },
  CLOSE_SETTING_MODAL(state) {
    state.showSettingModal = false
  },
  OPEN_SUMMARY_MODAL(state) {
    state.showSummaryModal = true
  },
  CLOSE_SUMMARY_MODAL(state) {
    state.showSummaryModal = false
  },
  SET_DICTATION_TOOLBAR(state, toolbar) {
    state.dictationToolbar = toolbar || {}
    state.dictationToolbarRevision = (state.dictationToolbarRevision || 0) + 1
    localStorage.setItem('dictationToolbar', JSON.stringify(state.dictationToolbar))
  },
  TOGGLE_ANSWER_TIP(state) {
    state.showAnswerTip = !state.showAnswerTip
  },
  SET_ANSWER_TIP(state, show) {
    state.showAnswerTip = show
  },
  SET_ERROR_TIP_SETTING(state, show) {
    state.showErrorTipSetting = show
  },
  SET_PLAYBACK_SETTINGS(state, payload) {
    const settings = payload?.settings ?? payload
    const silent = Boolean(payload?.silent)
    state.playbackSettings = { ...state.playbackSettings, ...settings }
    if (!silent) state.playbackSettingsRevision += 1
  },
  SHOW_QUESTION(state) {
    // 显示题目状态（对齐老项目）
    state.gameMode = GameMode.Question
    state.showAnswerTip = false
  },
  SHOW_ANSWER(state) {
    // 显示答案状态（对齐老项目）
    state.gameMode = GameMode.Answer
  },
  HIDE_ANSWER_TIP(state) {
    state.showAnswerTip = false
  },
  SHOW_ANSWER_TIP(state) {
    state.showAnswerTip = true
  },
  RESET(state) {
    state.statementIndex = 0
    state.showAnswerTip = false
    state.gameMode = GameMode.Question
  }
}

const actions = {
  setGamePlayMode({ commit }, mode) {
    commit('SET_GAME_PLAY_MODE', mode)
  },
  setStatementIndex({ commit }, index) {
    commit('SET_STATEMENT_INDEX', index)
  },
  setStatements({ commit }, statements) {
    commit('SET_STATEMENTS', statements)
  },
  openPauseModal({ commit }) {
    commit('OPEN_PAUSE_MODAL')
  },
  closePauseModal({ commit }) {
    commit('CLOSE_PAUSE_MODAL')
  },
  openSettingModal({ commit }) {
    commit('OPEN_SETTING_MODAL')
  },
  closeSettingModal({ commit }) {
    commit('CLOSE_SETTING_MODAL')
  },
  openSummaryModal({ commit }) {
    commit('OPEN_SUMMARY_MODAL')
  },
  closeSummaryModal({ commit }) {
    commit('CLOSE_SUMMARY_MODAL')
  },
  setDictationToolbar({ commit }, toolbar) {
    commit('SET_DICTATION_TOOLBAR', toolbar)
  },
  toggleAnswerTip({ commit }) {
    commit('TOGGLE_ANSWER_TIP')
  },
  setAnswerTip({ commit }, show) {
    commit('SET_ANSWER_TIP', show)
  },
  setErrorTipSetting({ commit }, show) {
    commit('SET_ERROR_TIP_SETTING', show)
  },
  setPlaybackSettings({ commit }, settings) {
    commit('SET_PLAYBACK_SETTINGS', settings)
  },
  toggleGamePlayMode({ commit }, mode) {
    commit('SET_GAME_PLAY_MODE', mode)
  },
  // 本地答案验证（对齐老项目）
  submitAnswer({ state, getters, commit }, payload) {
    const normalized = typeof payload === 'object' && payload !== null && 'userInput' in payload
      ? payload
      : { userInput: payload, skipShowAnswer: false }
    const userInput = normalized.userInput
    const skipShowAnswer = Boolean(normalized.skipShowAnswer)
    const currentStatement = getters.currentStatement
    if (!currentStatement) {
      return {
        correct: false,
        message: '没有当前题目',
        debug: { failReason: '没有当前题目' }
      }
    }

    const expectedRaw = resolveExpectedAnswerText(currentStatement)

    // 使用与老项目相同的逻辑提取单词部分
    // 只提取字母部分（以及单词中的撇号'和连字符-），数字和其他符号都忽略
    const extractWords = (sentence) => {
      if (!sentence) return ''

      // 弯引号先归一成直撇号，避免 en 里 let’s 被拆成 let + s
      const sentenceStr = String(sentence)
        .replace(/[\u2018\u2019]/g, "'")
      // 先移除括号（包括中文括号、英文括号、方括号、花括号）
      const withoutBrackets = sentenceStr.replace(/[\(\)（）\[\]{}]/g, ' ')
      // 使用 token 方式拆分：字母+撇号+连字符 或 其他字符
      const tokens = withoutBrackets.match(/([A-Za-z'\-]+|[^A-Za-z'\-\s]+)/g) || []

      const wordParts = []

      for (let i = 0; i < tokens.length; i++) {
        const raw = String(tokens[i] || '').trim()
        if (!raw) continue

        // 完全不包含字母的视为标点/数字，跳过
        const isPurePunct = /^[^A-Za-z]+$/.test(raw)
        if (isPurePunct) continue

        // 逐字符扫描，只提取字母（以及单词中的撇号'和连字符-）
        let currentWord = ''
        for (let j = 0; j < raw.length; j++) {
          const char = raw[j]
          const isLetter = /[A-Za-z]/.test(char)
          const isApostrophe = char === "'"
          const isHyphen = char === '-'
          
          if (isLetter || (currentWord && (isApostrophe || isHyphen))) {
            // 字母，或者单词中的撇号/连字符
            currentWord += char
          } else {
            // 其他字符（数字、标点等）：结束当前单词
            if (currentWord) {
              wordParts.push(currentWord)
              currentWord = ''
            }
          }
        }
        
        // 添加最后的单词
        if (currentWord) {
          wordParts.push(currentWord)
        }
      }

      // 用单个空格连接
      return wordParts.join(' ')
    }

    // 对期望值和用户输入都应用相同的提取逻辑
    const ignoreCase = Number(state.playbackSettings?.answer_ignore_case || 0) === 1
    const normalizeCase = (text) => {
      const base = String(text || '').trim()
      return ignoreCase ? base.toLowerCase() : base
    }
    const extractedExpected = extractWords(expectedRaw)
    const extractedActual = extractWords(userInput)
    const expected = normalizeCase(extractedExpected)
    const actual = normalizeCase(extractedActual)

    const isCorrect = expected && actual === expected

    const firstWordMismatch = (exp, act) => {
      const ew = String(exp || '').split(/\s+/).filter(Boolean)
      const aw = String(act || '').split(/\s+/).filter(Boolean)
      const max = Math.max(ew.length, aw.length)
      for (let i = 0; i < max; i++) {
        if (ew[i] !== aw[i]) {
          return {
            wordIndex: i,
            expectedWord: ew[i] !== undefined ? ew[i] : '(缺词)',
            actualWord: aw[i] !== undefined ? aw[i] : '(缺词)',
            expectedWordCount: ew.length,
            actualWordCount: aw.length
          }
        }
      }
      return null
    }

    const debug = {
      ignoreCase: !!ignoreCase,
      expectedRaw: String(expectedRaw),
      userInput: String(userInput ?? ''),
      extractedExpected,
      extractedActual,
      comparedExpected: expected,
      comparedActual: actual,
      firstMismatch: !isCorrect ? firstWordMismatch(expected, actual) : null,
      failReason: !isCorrect
        ? (!expected
          ? '标准句抽词后为空，无法判对'
          : actual !== expected
            ? '抽词并规范化后，用户串与标准串不一致'
            : '未知')
        : null
    }

    // 关键修复：对齐老项目，答对后调用 SHOW_ANSWER（答对自动下一题时跳过，避免闪答案态）
    if (isCorrect) {
      if (!skipShowAnswer) {
        commit('SHOW_ANSWER')
      }
      return { correct: true, debug }
    }

    return { correct: false, debug }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

